import { Scope } from './scope';
import { EngineLocator } from './engine-locator';
import { Document } from './document';
export class Collection {
    constructor(name, scope, database) {
        //used for engine calls
        this._engine = EngineLocator.getEngine(EngineLocator.key);
        this._didStartListener = false;
        /**
         * returns Collection's fully qualified name in the '<scope-name>.<collection-name>' format.
         *
         * @property
         */
        this.fullName = () => `${this.scope.name}.${this.name}`;
        this.name = name ?? '';
        this.scope = scope ?? new Scope('', database);
        this.database = database;
        this._documentChangeListener = new Map();
        this._didStartDocumentListener = new Map();
    }
    /**
     * Set a given CollectionChangeListener to the collection.
     *
     * @function
     */
    async addChangeListener(listener) {
        this._changeListener = listener;
        const token = this.uuid();
        if (!this._didStartListener) {
            await this._engine.collection_AddChangeListener({
                name: this.scope.database.getUniqueName(),
                scopeName: this.scope.name,
                collectionName: this.name,
                changeListenerToken: token,
            }, (data, err) => {
                if (err) {
                    throw err;
                }
                this.notifyChangeListeners(data);
            });
            this._didStartListener = true;
            return token;
        }
        else {
            throw new Error('Listener already started');
        }
    }
    /**
     * Set a given Collection Document Change Listener to a given document in a collection.
     *
     * @function
     */
    async addDocumentChangeListener(documentId, listener) {
        const token = this.uuid();
        if (!this._didStartDocumentListener.has(documentId) &&
            !this._documentChangeListener[documentId]) {
            await this._engine.collection_AddDocumentChangeListener({
                name: this.scope.database.getUniqueName(),
                scopeName: this.scope.name,
                collectionName: this.name,
                changeListenerToken: token,
                documentId: documentId,
            }, (data, err) => {
                if (err) {
                    throw err;
                }
                this.notifyDocumentChangeListeners(data);
            });
            this._documentChangeListener.set(documentId, listener);
            this._didStartDocumentListener[documentId] = true;
            return token;
        }
        else {
            throw new Error(`Listener for document ${documentId} already started`);
        }
    }
    /**
     * Total number of documents in the collection.
     *
     * @function
     */
    count() {
        return this._engine.collection_GetCount({
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
        });
    }
    /**
     * Create an index with the index name and AbstractIndex.
     *
     * @function
     */
    createIndex(indexName, index) {
        return this._engine.collection_CreateIndex({
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            indexName: indexName,
            index: index.toJson(),
        });
    }
    /**
     * Delete a document from the collection. The default concurrency control, lastWriteWins,
     * will be used when there is conflict during delete. If the document doesn’t exist in the
     * collection, an error will be thrown.
     *
     * When deleting a document that already belongs to a collection, the collection instance of
     * the document and this collection instance must be the same, otherwise, an
     * error will be thrown.
     *
     * Throws an Error if the collection is deleted or the database is closed.
     *
     * @function
     */
    deleteDocument(document, concurrencyControl = null) {
        const id = document.getId();
        return this._engine.collection_DeleteDocument({
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            docId: id,
            concurrencyControl: concurrencyControl,
        });
    }
    /**
     * Delete an index by name.
     *
     * @function
     */
    deleteIndex(indexName) {
        return this._engine.collection_DeleteIndex({
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            indexName: indexName,
        });
    }
    /**
     * Get an existing document by document ID.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    async document(id) {
        const docJson = await this._engine.collection_GetDocument({
            docId: id,
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
        });
        // @ts-expect-error - _id is used in getId()
        if (docJson && docJson._id) {
            // @ts-expect-error - _data is used in getData()
            const data = docJson._data;
            // @ts-expect-error - _sequence is used in getSequence()
            const sequence = docJson._sequence;
            // @ts-expect-error - _id is used in getId()
            const retId = docJson._id;
            // @ts-expect-error - _revId is used in getRevisionID()
            const revisionID = docJson._revId;
            return Promise.resolve(new Document(retId, sequence, revisionID, this, data));
        }
        else {
            return Promise.resolve(undefined);
        }
    }
    /**
     * Get an existing document by document ID.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    async getDocument(id) {
        return this.document(id);
    }
    /**
       * Get the expiration date set to the document of the given id.
  
       * Throws an error, if the collection is deleted or the database is closed.
       *
       * @function
       */
    async getDocumentExpiration(id) {
        const date = await this._engine.collection_GetDocumentExpiration({
            docId: id,
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
        });
        if (!!date && date?.date) {
            return date.date;
        }
        else {
            return null;
        }
    }
    /**
     * Return ICoreEngine instance.
     *
     * @function
     */
    getEngine() {
        return this._engine;
    }
    /**
     * Return all index names
     *
     * @function
     */
    async indexes() {
        const indexes = await this._engine.collection_GetIndexes({
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
        });
        return indexes.indexes;
    }
    /**
     * send data to the listener
     *
     * @function
     */
    notifyChangeListeners(data) {
        this._changeListener(data);
    }
    /**
     * send data to the document change listener
     *
     * @function
     */
    notifyDocumentChangeListeners(data) {
        const documentId = data.documentId;
        const changeListener = this._documentChangeListener.get(documentId);
        changeListener({
            documentId: documentId,
            collection: this,
            database: this.database,
        });
    }
    /**
     * Purge a document by id from the collection. If the document doesn’t exist in the
     * collection, an error will be thrown.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    purge(document) {
        return this._engine.collection_PurgeDocument({
            name: this.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            docId: document.getId(),
        });
    }
    /**
     * Purge a document by id from the collection. If the document doesn’t exist in the
     * collection, an error will be thrown.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    purgeById(documentId) {
        return this._engine.collection_PurgeDocument({
            name: this.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            docId: documentId,
        });
    }
    /**
     * Remove the collection change listener.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    async removeChangeListener(token) {
        await this._engine.collection_RemoveChangeListener({
            name: this.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            changeListenerToken: token,
        });
    }
    /**
     * Remove the collection document change listener.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    async removeDocumentChangeListener(token) {
        await this._engine.collection_RemoveDocumentChangeListener({
            name: this.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            changeListenerToken: token,
        });
    }
    /**
     * Save a document into the collection. The default concurrency control, lastWriteWins, will
     * be used when there is conflict during save.
     *
     * When saving a document that already belongs to a collection, the collection instance of
     * the document and this collection instance must be the same, otherwise, an
     * error will be thrown.
     *
     * Throws an error if the collection is deleted or the database is closed.
     *
     * @function
     */
    async save(document, concurrencyControl = null) {
        const blobString = document.blobsToJsonString();
        const documentString = document.toJsonString();
        const ret = await this._engine.collection_Save({
            id: document.getId(),
            document: documentString,
            blobs: blobString,
            concurrencyControl: concurrencyControl,
            name: this.scope.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
        });
        document.setId(ret._id);
        document.setRevisionID(ret._revId);
        document.setSequence(ret._sequence);
        document.setCollection(this);
    }
    async setDocumentExpiration(id, expiration) {
        await this._engine.collection_SetDocumentExpiration({
            docId: id,
            name: this.database.getUniqueName(),
            scopeName: this.scope.name,
            collectionName: this.name,
            expiration: expiration,
        });
    }
    toJson() {
        return {
            name: this.name,
            scopeName: this.scope.name,
            databaseName: this.database.getUniqueName(),
        };
    }
    uuid() {
        return this._engine.getUUID();
    }
}
//# sourceMappingURL=collection.js.map